<template>
    <div class="py-4 container-fluid">
        <!-- Alert Message -->
        <div v-if="showAlert" class="alert alert-dismissible fade show" :class="'alert-' + alertColor" role="alert">
            <i :class="alertColor === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'" class="me-2"></i>
            {{ alertMessage }}
            <button type="button" class="btn-close" @click="showAlert = false"></button>
        </div>
        
        <div class="row">
            <div class="col-12">
                <!-- Header Card with Tabs -->
                <div class="card mb-4 header-card">
                    <div class="card-body py-3 px-4">
                        <div class="row align-items-center">
                            <div class="col-lg-5 col-md-12 mb-3 mb-lg-0">
                                <div class="d-flex align-items-center">
                                    <div class="icon-box me-3">
                                        <i class="fas fa-file-invoice-dollar"></i>
                                    </div>
                                    <div>
                                        <h5 class="mb-0 fw-bold">Pension Exports</h5>
                                        <p class="text-sm text-muted mb-0">Katsina State Government</p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-lg-7 col-md-12">
                                <ul class="nav nav-pills-custom nav-fill" role="tablist">
                                    <li class="nav-item">
                                        <a class="nav-link" :class="{ active: activeTab === 'generate' }" 
                                            @click="activeTab = 'generate'" href="javascript:;">
                                            <i class="fas fa-cog me-1"></i> Generate Report
                                        </a>
                                    </li>
                                    <li class="nav-item">
                                        <a class="nav-link" :class="{ active: activeTab === 'history' }" 
                                            @click="activeTab = 'history'" href="javascript:;">
                                            <i class="fas fa-history me-1"></i> Report History
                                            <span class="badge bg-info ms-1">{{ reportHistory.length }}</span>
                                        </a>
                                    </li>
                                    <li class="nav-item">
                                        <a class="nav-link" :class="{ active: activeTab === 'update' }" 
                                            @click="activeTab = 'update'" href="javascript:;">
                                            <i class="fas fa-upload me-1"></i> Pension Update
                                            <span class="badge bg-warning ms-1">{{ payrollUpdates.length }}</span>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Generate Report Tab -->
                <div v-if="activeTab === 'generate'" class="card">
                    <div class="card-header">
                        <h6 class="mb-0"><i class="fas fa-filter me-2"></i>Filter Options</h6>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <!-- Row 0: Basic Search Filters (Currently Supported) -->
                            <div class="col-md-6 mb-3">
                                <label class="form-label text-xs">Search by Name</label>
                                <input type="text" class="form-control form-control-sm" v-model="filters.name" placeholder="Enter pensioner name...">
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Phone Number</label>
                                <input type="text" class="form-control form-control-sm" v-model="filters.phone_no" placeholder="Enter phone number...">
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Updated After (Date)</label>
                                <input type="date" class="form-control form-control-sm" v-model="filters.updated_at">
                            </div>

                            <!-- Row 1: Date Filters -->
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Retirement Year</label>
                                <select class="form-control form-control-sm" v-model="filters.retirementYear">
                                    <option value="">Any Year</option>
                                    <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                                </select>
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Retirement Month</label>
                                <select class="form-control form-control-sm" v-model="filters.retirementMonth">
                                    <option value="">Any Month</option>
                                    <option v-for="(month, index) in months" :key="index" :value="index + 1">{{ month }}</option>
                                </select>
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">
                                    Pensioner Type
                                    <span v-if="!filters.dualPensionersOnly" class="text-danger">*</span>
                                </label>
                                <select class="form-control form-control-sm" 
                                    v-model="filters.pensionerType"
                                    :class="{ 'border-warning': !filters.dualPensionersOnly && !filters.pensionerType }">
                                    <option value="">-- Select Type --</option>
                                    <option value="state">State Pensioner</option>
                                    <option value="lga">LGA Pensioner</option>
                                </select>
                                <small v-if="!filters.dualPensionersOnly && !filters.pensionerType" class="text-warning">
                                    Required for pension export
                                </small>
                            </div>

                            <!-- Row 2: Additional Filters -->
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Min Pension Amount (₦)</label>
                                <input type="number" class="form-control form-control-sm" v-model="filters.minPension" placeholder="0">
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Max Pension Amount (₦)</label>
                                <input type="number" class="form-control form-control-sm" v-model="filters.maxPension" placeholder="0">
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Bank</label>
                                <select class="form-control form-control-sm" v-model="filters.bank">
                                    <option value="">All Banks</option>
                                    <option v-for="bank in banks" :key="bank" :value="bank">{{ bank }}</option>
                                </select>
                            </div>

                            <!-- Row 3: LGA and Dual Pensioners -->
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">LGA</label>
                                <select class="form-control form-control-sm" v-model="filters.lga">
                                    <option value="">All LGAs</option>
                                    <option v-for="lga in lgas" :key="lga" :value="lga">{{ lga }}</option>
                                </select>
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Report Type</label>
                                <select class="form-control form-control-sm" v-model="filters.reportType">
                                    <option value="summarized">Summarized</option>
                                    <option value="detailed">Detailed</option>
                                </select>
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="form-label text-xs">Grade Level</label>
                                <input type="text" class="form-control form-control-sm" v-model="filters.gradeLevel" placeholder="e.g. GL 14">
                            </div>
                            <div class="col-md-3 mb-3">&nbsp;</div>
                            <div class="col-md-6 mb-3">
                                <label class="form-label text-xs">&nbsp;</label>
                                <div class="form-check mt-2">
                                    <input class="form-check-input" type="checkbox" v-model="filters.dualPensionersOnly" id="dualPensioners">
                                    <label class="form-check-label" for="dualPensioners">
                                        <strong>Dual Pensioners Only</strong>
                                        <br><small class="text-muted">Pensioners with both LGA and State pensions</small>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <hr>

                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <span class="text-sm text-muted d-block">
                                    <i class="fas fa-info-circle me-1"></i>
                                    Reports are generated as Excel files and saved to your history.
                                </span>
                                <span class="text-sm text-success">
                                    <i class="fas fa-check-circle me-1"></i>
                                    <strong>Note:</strong> Only verified pensioners are included in pension exports.
                                </span>
                            </div>
                            <div>
                                <button class="btn btn-sm btn-outline-secondary me-2" @click="resetFilters">
                                    <i class="fas fa-redo me-1"></i> Reset Filters
                                </button>
                                <button class="btn btn-sm bg-gradient-success" @click="generateReport" :disabled="isGenerating">
                                    <span v-if="isGenerating">
                                        <i class="fas fa-spinner fa-spin me-1"></i> Generating...
                                    </span>
                                    <span v-else>
                                        <i class="fas fa-file-excel me-1"></i> Generate Report
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Report History Tab -->
                <div v-if="activeTab === 'history'" class="card">
                    <div class="card-header">
                        <div class="row align-items-center">
                            <div class="col-md-8">
                                <h6 class="mb-0"><i class="fas fa-history me-2"></i>Report History</h6>
                                <p class="text-xs text-muted mb-0">View and download your previously generated reports</p>
                            </div>
                            <div class="col-md-4 text-end">
                                <button class="btn btn-sm btn-outline-info" @click="refreshHistory">
                                    <i class="fas fa-sync-alt me-1"></i> Refresh
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body p-0">
                        <div class="table-responsive">
                            <table class="table align-items-center mb-0">
                                <thead class="bg-light">
                                    <tr>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Report Type</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Created By</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Date Created</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Filters Applied</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Status</th>
                                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="report in reportHistory" :key="report.id">
                                        <td>
                                            <span class="badge" :class="report.type === 'Summarized' ? 'bg-info' : 'bg-primary'">
                                                {{ report.type }}
                                            </span>
                                        </td>
                                        <td><span class="text-xs font-weight-bold">{{ report.createdBy }}</span></td>
                                        <td><span class="text-xs">{{ report.dateCreated }}</span></td>
                                        <td>
                                            <div class="d-flex flex-wrap gap-1">
                                                <span v-for="(filter, index) in report.filtersApplied" 
                                                    :key="index" 
                                                    class="badge bg-light text-dark border text-xxs">
                                                    {{ filter }}
                                                </span>
                                                <span v-if="!report.filtersApplied || report.filtersApplied.length === 0" 
                                                    class="text-xs text-muted">
                                                    Verified pensioners
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <vsud-badge v-if="report.status === 'Completed'" color="success" variant="gradient" size="sm">
                                                <i class="fas fa-check me-1"></i> Completed
                                            </vsud-badge>
                                            <vsud-badge v-else-if="report.status === 'Pending'" color="warning" variant="gradient" size="sm">
                                                <i class="fas fa-spinner fa-spin me-1"></i> Processing...
                                            </vsud-badge>
                                            <vsud-badge v-else color="danger" variant="gradient" size="sm">
                                                <i class="fas fa-times me-1"></i> Failed
                                            </vsud-badge>
                                        </td>
                                        <td>
                                            <button v-if="report.status === 'Completed'" 
                                                class="btn btn-link text-success p-1" title="Download"
                                                @click="downloadReport(report)">
                                                <i class="fas fa-download"></i> Download
                                            </button>
                                            <span v-else-if="report.status === 'Pending'" class="text-muted text-xs">
                                                Processing...
                                            </span>
                                            <span v-else class="text-danger text-xs">
                                                Generation failed
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="reportHistory.length === 0">
                                        <td colspan="6" class="text-center py-4">
                                            <i class="fas fa-folder-open text-muted fa-2x mb-2"></i>
                                            <p class="text-muted mb-0">No reports generated yet</p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Pension Update tab -->
                <div v-if="activeTab === 'update'">
                    <!-- Upload Card -->
                    <div class="card mb-4">
                        <div class="card-header">
                            <h6 class="mb-0"><i class="fas fa-upload me-2"></i>Pension Update</h6>
                            <p class="text-xs text-muted mb-0">Update pension information for pensioners using CSV file</p>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="upload-area border-dashed rounded p-4 text-center" 
                                        @dragover.prevent 
                                        @drop.prevent="handleFileDrop"
                                        :class="{ 'bg-light': isDragging }">
                                        <i class="fas fa-cloud-upload-alt fa-3x text-info mb-3"></i>
                                        <h6>Drag & Drop CSV File Here</h6>
                                        <p class="text-sm text-muted mb-3">or</p>
                                        <input type="file" ref="fileInput" accept=".csv" @change="handleFileSelect" class="d-none">
                                        <button class="btn btn-sm bg-gradient-info" @click="$refs.fileInput.click()">
                                            <i class="fas fa-folder-open me-1"></i> Browse Files
                                        </button>
                                        <p class="text-xs text-muted mt-2 mb-0">
                                            Only CSV files are accepted. Max file size: 10MB
                                        </p>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="bg-light rounded p-4">
                                        <h6><i class="fas fa-info-circle me-2"></i>CSV Template Format</h6>
                                        <p class="text-sm mb-2">Your CSV file should contain the following columns:</p>
                                        <table class="table table-sm table-bordered">
                                            <thead class="bg-dark text-white">
                                                <tr>
                                                    <th class="text-xs">Column</th>
                                                    <th class="text-xs">Required</th>
                                                    <th class="text-xs">Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td class="text-xs">pension_id</td>
                                                    <td><span class="badge bg-danger">Yes</span></td>
                                                    <td class="text-xs">Unique identifier (e.g., KT-PEN-ST-0001)</td>
                                                </tr>
                                                <tr>
                                                    <td class="text-xs">bvn</td>
                                                    <td><span class="badge bg-danger">Yes</span></td>
                                                    <td class="text-xs">11-digit BVN</td>
                                                </tr>
                                                <tr>
                                                    <td class="text-xs">bank_name</td>
                                                    <td><span class="badge bg-warning">Optional</span></td>
                                                    <td class="text-xs">Bank name</td>
                                                </tr>
                                                <tr>
                                                    <td class="text-xs">account_number</td>
                                                    <td><span class="badge bg-warning">Optional</span></td>
                                                    <td class="text-xs">10-digit account number</td>
                                                </tr>
                                                <tr>
                                                    <td class="text-xs">pension_amount</td>
                                                    <td><span class="badge bg-warning">Optional</span></td>
                                                    <td class="text-xs">Monthly pension amount</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <button class="btn btn-sm btn-outline-dark mt-2" @click="downloadTemplate">
                                            <i class="fas fa-download me-1"></i> Download Template
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Selected File Preview -->
                            <div v-if="selectedFile" class="mt-4 p-3 bg-light rounded">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div>
                                        <i class="fas fa-file-csv text-success me-2"></i>
                                        <strong>{{ selectedFile.name }}</strong>
                                        <span class="text-muted ms-2">({{ formatFileSize(selectedFile.size) }})</span>
                                    </div>
                                    <div>
                                        <button class="btn btn-sm btn-outline-danger me-2" @click="selectedFile = null">
                                            <i class="fas fa-times"></i> Remove
                                        </button>
                                        <button class="btn btn-sm bg-gradient-success" @click="uploadFile" :disabled="isUploading">
                                            <span v-if="isUploading">
                                                <i class="fas fa-spinner fa-spin me-1"></i> Uploading...
                                            </span>
                                            <span v-else>
                                                <i class="fas fa-upload me-1"></i> Upload & Process
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Upload History -->
                    <div class="card">
                        <div class="card-header">
                            <h6 class="mb-0"><i class="fas fa-list me-2"></i>Upload History</h6>
                        </div>
                        <div class="card-body p-0">
                            <div class="table-responsive">
                                <table class="table align-items-center mb-0">
                                    <thead class="bg-light">
                                        <tr>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">File Name</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Uploaded By</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Date Uploaded</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Records</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Status</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="upload in payrollUpdates" :key="upload.id">
                                            <td><span class="text-xs font-weight-bold">{{ upload.fileName }}</span></td>
                                            <td><span class="text-xs">{{ upload.uploadedBy }}</span></td>
                                            <td><span class="text-xs">{{ upload.dateUploaded }}</span></td>
                                            <td>
                                                <span class="text-xs">
                                                    <span class="text-success">{{ upload.successCount }}</span> / 
                                                    <span class="text-danger">{{ upload.errorCount }}</span> / 
                                                    <span class="text-muted">{{ upload.totalCount }}</span>
                                                </span>
                                            </td>
                                            <td>
                                                <vsud-badge :color="getUploadStatusColor(upload.status)" variant="gradient" size="sm">
                                                    {{ upload.status }}
                                                </vsud-badge>
                                            </td>
                                            <td>
                                                <button class="btn btn-link text-info p-1" title="View Logs" @click="viewLogs(upload)">
                                                    <i class="fas fa-file-alt"></i> View Logs
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="payrollUpdates.length === 0">
                                            <td colspan="6" class="text-center py-4">
                                                <i class="fas fa-upload text-muted fa-2x mb-2"></i>
                                                <p class="text-muted mb-0">No uploads yet</p>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- View Logs Modal -->
        <div class="modal fade" :class="{ 'show d-block': showLogsModal }" tabindex="-1" v-if="showLogsModal">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Upload Logs - {{ selectedUpload?.fileName }}</h5>
                        <button type="button" class="btn-close" @click="showLogsModal = false"></button>
                    </div>
                    <div class="modal-body">
                        <div class="mb-3">
                            <div class="row text-center">
                                <div class="col-4">
                                    <div class="bg-success text-white rounded p-3">
                                        <h4 class="mb-0">{{ selectedUpload?.successCount || 0 }}</h4>
                                        <small>Successful</small>
                                    </div>
                                </div>
                                <div class="col-4">
                                    <div class="bg-danger text-white rounded p-3">
                                        <h4 class="mb-0">{{ selectedUpload?.errorCount || 0 }}</h4>
                                        <small>Failed</small>
                                    </div>
                                </div>
                                <div class="col-4">
                                    <div class="bg-info text-white rounded p-3">
                                        <h4 class="mb-0">{{ selectedUpload?.totalCount || 0 }}</h4>
                                        <small>Total</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="table-responsive" style="max-height: 400px;">
                            <table class="table table-sm">
                                <thead class="sticky-top bg-white">
                                    <tr>
                                        <th>Row</th>
                                        <th>Pension ID</th>
                                        <th>BVN</th>
                                        <th>Status</th>
                                        <th>Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(log, index) in uploadLogs" :key="index" 
                                        :class="log.status === 'success' ? 'table-success' : 'table-danger'">
                                        <td>{{ log.row }}</td>
                                        <td>{{ log.pensionId }}</td>
                                        <td>{{ log.bvn }}</td>
                                        <td>
                                            <span :class="log.status === 'success' ? 'text-success' : 'text-danger'">
                                                {{ log.status }}
                                            </span>
                                        </td>
                                        <td class="text-xs">{{ log.message }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-sm btn-outline-info" @click="exportLogs">
                            <i class="fas fa-download me-1"></i> Export Logs
                        </button>
                        <button type="button" class="btn btn-secondary" @click="showLogsModal = false">Close</button>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop fade show" v-if="showLogsModal" @click="showLogsModal = false"></div>
    </div>
</template>

<script>
import VsudBadge from "@/components/VsudBadge.vue";
import AuthService from "@/services/auth.service.js";

export default {
    name: "payroll-reports",
    data() {
        return {
            activeTab: 'generate',
            isGenerating: false,
            isUploading: false,
            isDragging: false,
            isLoadingHistory: false,
            selectedFile: null,
            showLogsModal: false,
            selectedUpload: null,
            uploadLogs: [],
            alertMessage: '',
            alertColor: 'success',
            showAlert: false,
            pollingIntervals: {},
            
            // Filters
            filters: {
                // Basic filters (currently supported by backend)
                name: '',
                updated_at: '',
                phone_no: '',
                // Advanced filters
                retirementYear: '',
                retirementMonth: '',
                pensionerType: '',
                minPension: '',
                maxPension: '',
                bank: '',
                lga: '',
                gradeLevel: '',
                dualPensionersOnly: false,
                reportType: 'summarized'
            },

            // Dropdown options
            years: Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i),
            months: ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'],
            banks: ['Access Bank', 'First Bank', 'GTBank', 'UBA', 'Zenith Bank', 
                    'Union Bank', 'Fidelity Bank', 'Keystone Bank', 'Unity Bank', 
                    'Ecobank', 'FCMB', 'Stanbic IBTC', 'Sterling Bank', 'Wema Bank'],
            lgas: ['Bakori', 'Batagarawa', 'Batsari', 'Baure', 'Bindawa', 'Charanchi',
                   'Dandume', 'Danja', 'Dan Musa', 'Daura', 'Dutsi', 'Dutsin-Ma',
                   'Faskari', 'Funtua', 'Ingawa', 'Jibia', 'Kafur', 'Kaita',
                   'Kankara', 'Kankia', 'Katsina', 'Kurfi', 'Kusada', 'Mai\'Adua',
                   'Malumfashi', 'Mani', 'Mashi', 'Matazu', 'Musawa', 'Rimi',
                   'Sabuwa', 'Safana', 'Sandamu', 'Zango'],

            // Session-based report history (not persisted - for current session only)
            reportHistory: [],

            // Payroll updates - will be populated from API when backend is ready
            payrollUpdates: []
        };
    },
    components: {
        VsudBadge
    },
    mounted() {
        this.loadPayrollHistory();
    },
    beforeUnmount() {
        // Clear all polling intervals when component is destroyed
        Object.values(this.pollingIntervals).forEach(interval => clearInterval(interval));
    },
    methods: {
        resetFilters() {
            this.filters = {
                name: '',
                updated_at: '',
                phone_no: '',
                retirementYear: '',
                retirementMonth: '',
                pensionerType: '',
                minPension: '',
                maxPension: '',
                bank: '',
                lga: '',
                gradeLevel: '',
                dualPensionersOnly: false,
                reportType: 'summarized'
            };
        },

        async generateReport() {
            this.isGenerating = true;
            this.showAlert = false;
            
            try {
                // Validate: If not duals export, user must select State or LGA
                if (!this.filters.dualPensionersOnly && !this.filters.pensionerType) {
                    this.alertMessage = 'Please select a Pensioner Type (State or LGA) to generate the report.';
                    this.alertColor = 'warning';
                    this.showAlert = true;
                    this.isGenerating = false;
                    return;
                }
                
                // Build query parameters for payroll export
                const params = new URLSearchParams();
                
                // Set file type (default to xlsx)
                params.append('type', 'xlsx');
                
                // Generate filename with date
                const date = new Date().toISOString().split('T')[0];
                const reportTypeLabel = this.filters.reportType === 'summarized' ? 'summarized' : 'detailed';
                const dualsLabel = this.filters.dualPensionersOnly ? '-duals' : '';
                const typeLabel = this.filters.pensionerType ? `-${this.filters.pensionerType}` : '';
                params.append('filename', `pension-${reportTypeLabel}${dualsLabel}${typeLabel}-${date}`);
                
                // Add duals_only parameter if checked
                if (this.filters.dualPensionersOnly) {
                    params.append('duals_only', '1');
                } else {
                    // Backend requires is_lga when not exporting duals
                    params.append('is_lga', this.filters.pensionerType === 'lga' ? '1' : '0');
                }
                
                // Add additional filters supported by backend
                if (this.filters.name) {
                    params.append('name', this.filters.name);
                }
                if (this.filters.phone_no) {
                    params.append('phone_no', this.filters.phone_no);
                }
                if (this.filters.updated_at) {
                    params.append('updated_at', this.filters.updated_at);
                }
                if (this.filters.retirementYear) {
                    params.append('retirement_year', this.filters.retirementYear);
                }
                if (this.filters.retirementMonth) {
                    params.append('retirement_month', this.filters.retirementMonth);
                }
                if (this.filters.minPension) {
                    params.append('min_amount', this.filters.minPension);
                }
                if (this.filters.maxPension) {
                    params.append('max_amount', this.filters.maxPension);
                }
                if (this.filters.bank) {
                    params.append('bank', this.filters.bank);
                }
                if (this.filters.lga) {
                    params.append('lga', this.filters.lga);
                }
                if (this.filters.gradeLevel) {
                    params.append('grade_level', this.filters.gradeLevel);
                }
                
                const queryString = '?' + params.toString();
                
                console.log('=== Payroll Export Request ===');
                console.log('Query String:', queryString);
                console.log('Report Type:', this.filters.reportType);
                console.log('Duals Only:', this.filters.dualPensionersOnly);
                console.log('Filters Applied:', this.filters);
                
                // Call the appropriate payroll export endpoint based on report type
                let response;
                if (this.filters.reportType === 'summarized') {
                    response = await AuthService.exportPayrollSummarized(queryString);
                } else {
                    response = await AuthService.exportPayroll(queryString);
                }
                
                // Check if response is a queued export (JSON with export_id) or immediate download (blob)
                // For duals_only=true, we get immediate file download
                // For full export (duals_only=false), we get JSON with export_id
                if (this.filters.dualPensionersOnly) {
                    // Immediate download for duals
                    this.downloadBlobAsFile(response, `pension-${reportTypeLabel}${dualsLabel}-${date}.xlsx`);

                    this.alertMessage = 'Pension report generated and downloaded successfully!';
                    this.alertColor = 'success';
                    this.showAlert = true;
                } else {
                    // Queued export - parse response to get export_id
                    // The response might be a Blob that contains JSON, so we need to read it
                    let jsonResponse;
                    if (response instanceof Blob) {
                        const text = await response.text();
                        try {
                            jsonResponse = JSON.parse(text);
                        } catch (e) {
                            // It's actually a file, not JSON - download it
                            this.downloadBlobAsFile(response, `pension-${reportTypeLabel}${dualsLabel}-${date}.xlsx`);
                            this.alertMessage = 'Pension report generated and downloaded successfully!';
                            this.alertColor = 'success';
                            this.showAlert = true;
                            return;
                        }
                    } else {
                        jsonResponse = response;
                    }
                    
                    if (jsonResponse && jsonResponse.export_id) {
                        // Export is queued - refresh history and start polling
                        this.alertMessage = 'Report is being generated in the background. Check the History tab for status.';
                        this.alertColor = 'info';
                        this.showAlert = true;
                        
                        // Switch to history tab and refresh
                        this.activeTab = 'history';
                        await this.loadPayrollHistory();
                        
                        // Start polling for this specific export
                        this.startPollingForExport(jsonResponse.export_id);
                    } else {
                        this.alertMessage = 'Report queued successfully. Check History tab for status.';
                        this.alertColor = 'info';
                        this.showAlert = true;
                        await this.loadPayrollHistory();
                    }
                }
                
            } catch (error) {
                console.error('Error generating payroll report:', error);
                this.alertMessage = `Failed to generate pension report: ${error.message || 'Please try again.'}`;
                this.alertColor = 'danger';
                this.showAlert = true;
                AuthService.CheckResponseError(error);
            } finally {
                this.isGenerating = false;
                setTimeout(() => { this.showAlert = false; }, 8000);
            }
        },
        
        downloadBlobAsFile(blob, filename) {
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        },

        getFiltersDescription() {
            const parts = ['Verified only'];  // Always verified pensioners
            if (this.filters.dualPensionersOnly) {
                parts.push('Dual pensioners');
            } else if (this.filters.pensionerType) {
                parts.push(this.filters.pensionerType === 'lga' ? 'LGA' : 'State');
            }
            if (this.filters.name) parts.push('Name: ' + this.filters.name);
            if (this.filters.retirementYear) parts.push('Year: ' + this.filters.retirementYear);
            if (this.filters.retirementMonth) parts.push('Month: ' + this.months[this.filters.retirementMonth - 1]);
            if (this.filters.bank) parts.push('Bank: ' + this.filters.bank);
            if (this.filters.lga) parts.push('LGA: ' + this.filters.lga);
            if (this.filters.gradeLevel) parts.push('Grade: ' + this.filters.gradeLevel);
            if (this.filters.minPension || this.filters.maxPension) {
                const range = [];
                if (this.filters.minPension) range.push('≥₦' + this.filters.minPension);
                if (this.filters.maxPension) range.push('≤₦' + this.filters.maxPension);
                parts.push('Pension: ' + range.join(' & '));
            }
            return parts.length > 0 ? parts.join(', ') : 'All pensioners';
        },

        async refreshHistory() {
            await this.loadPayrollHistory();
            this.alertMessage = 'History refreshed successfully!';
            this.alertColor = 'success';
            this.showAlert = true;
            setTimeout(() => { this.showAlert = false; }, 3000);
        },

        async loadPayrollHistory() {
            this.isLoadingHistory = true;
            try {
                const response = await AuthService.getPayrollHistory('?per_page=50');
                if (response && response.data) {
                    this.reportHistory = response.data.map(item => ({
                        id: item.id,
                        type: item.type === 'summarized' ? 'Summarized' : 'Detailed',
                        createdBy: item.user?.name || 'System',
                        dateCreated: this.formatDateTime(item.created_at),
                        filtersApplied: this.formatFiltersFromApi(item.filters),
                        status: this.capitalizeFirst(item.status),
                        filename: item.filename
                    }));
                    
                    // Start polling for any pending reports
                    this.reportHistory
                        .filter(r => r.status === 'Pending')
                        .forEach(r => this.startPollingForExport(r.id));
                }
            } catch (error) {
                console.error('Error loading payroll history:', error);
                AuthService.CheckResponseError(error);
            } finally {
                this.isLoadingHistory = false;
            }
        },
        
        startPollingForExport(exportId) {
            // Don't start duplicate polling
            if (this.pollingIntervals[exportId]) return;
            
            // Poll every 5 seconds
            this.pollingIntervals[exportId] = setInterval(async () => {
                try {
                    const response = await AuthService.getPayrollHistoryById(exportId);
                    if (response && response.data) {
                        const status = response.data.status;
                        
                        // Update the report in our list
                        const reportIndex = this.reportHistory.findIndex(r => r.id === exportId);
                        if (reportIndex !== -1) {
                            this.reportHistory[reportIndex].status = this.capitalizeFirst(status);
                        }
                        
                        // Stop polling if completed or failed
                        if (status === 'completed' || status === 'failed') {
                            clearInterval(this.pollingIntervals[exportId]);
                            delete this.pollingIntervals[exportId];
                            
                            if (status === 'completed') {
                                this.alertMessage = 'Report generation completed! Click Download to get your file.';
                                this.alertColor = 'success';
                                this.showAlert = true;
                                setTimeout(() => { this.showAlert = false; }, 5000);
                            } else {
                                this.alertMessage = 'Report generation failed. Please try again.';
                                this.alertColor = 'danger';
                                this.showAlert = true;
                                setTimeout(() => { this.showAlert = false; }, 5000);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error polling export status:', error);
                }
            }, 5000);
        },

        async downloadReport(report) {
            try {
                const response = await AuthService.downloadPayrollHistory(report.id);
                
                // Backend returns JSON with URL
                if (response && response.url) {
                    // Trigger download via an anchor click (more reliable than
                    // window.open, which popup blockers can silently block)
                    const link = document.createElement('a');
                    link.href = response.url;
                    link.target = '_blank';
                    link.rel = 'noopener';
                    link.download = '';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    this.alertMessage = 'Report download started!';
                    this.alertColor = 'success';
                    this.showAlert = true;
                    setTimeout(() => { this.showAlert = false; }, 3000);
                } else {
                    throw new Error('No download URL received from server');
                }
            } catch (error) {
                console.error('Error downloading report:', error);
                this.alertMessage = error.response?.data?.message || 'Failed to download report. Please try again.';
                this.alertColor = 'danger';
                this.showAlert = true;
                AuthService.CheckResponseError(error);
            }
        },
        
        formatDateTime(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleString();
        },
        
        capitalizeFirst(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        },

        formatFiltersFromApi(filters) {
            if (!filters) return ['Verified only'];
            
            // Parse filters if it's a string (JSON)
            let filterObj = filters;
            if (typeof filters === 'string') {
                try {
                    filterObj = JSON.parse(filters);
                } catch (e) {
                    return ['Verified only'];
                }
            }
            
            const parts = ['Verified only'];
            
            // Type filter labels
            const typeLabels = {
                '0': 'State',
                '1': 'LGA',
                'state': 'State',
                'lga': 'LGA'
            };
            
            // Duals only
            if (filterObj.duals_only === '1' || filterObj.duals_only === true) {
                parts.push('Dual Pensioners');
            }
            
            // Pensioner Type (is_lga)
            if (filterObj.is_lga !== undefined && filterObj.is_lga !== '') {
                parts.push(typeLabels[filterObj.is_lga] || filterObj.is_lga);
            }
            
            // Name filter
            if (filterObj.name) {
                parts.push(`Name: ${filterObj.name}`);
            }
            
            // Retirement Year
            if (filterObj.retirement_year) {
                parts.push(`Year: ${filterObj.retirement_year}`);
            }
            
            // Retirement Month
            if (filterObj.retirement_month) {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthIndex = parseInt(filterObj.retirement_month) - 1;
                parts.push(`Month: ${monthNames[monthIndex] || filterObj.retirement_month}`);
            }
            
            // Bank
            if (filterObj.bank) {
                parts.push(`Bank: ${filterObj.bank}`);
            }
            
            // LGA
            if (filterObj.lga) {
                parts.push(`LGA: ${filterObj.lga}`);
            }
            
            // Grade Level
            if (filterObj.grade_level) {
                parts.push(`Grade: ${filterObj.grade_level}`);
            }
            
            // Min/Max Amount
            if (filterObj.min_amount || filterObj.max_amount) {
                let range = 'Pension: ';
                if (filterObj.min_amount) range += `≥₦${parseInt(filterObj.min_amount).toLocaleString()}`;
                if (filterObj.min_amount && filterObj.max_amount) range += ' - ';
                if (filterObj.max_amount) range += `≤₦${parseInt(filterObj.max_amount).toLocaleString()}`;
                parts.push(range);
            }
            
            // Phone
            if (filterObj.phone_no) {
                parts.push(`Phone: ${filterObj.phone_no}`);
            }
            
            // Updated at
            if (filterObj.updated_at) {
                parts.push(`Updated: ${filterObj.updated_at}`);
            }
            
            return parts;
        },

        handleFileDrop(event) {
            this.isDragging = false;
            const files = event.dataTransfer.files;
            if (files.length > 0 && files[0].type === 'text/csv') {
                this.selectedFile = files[0];
            } else {
                alert('Please upload a CSV file');
            }
        },

        handleFileSelect(event) {
            const files = event.target.files;
            if (files.length > 0) {
                this.selectedFile = files[0];
            }
        },

        formatFileSize(bytes) {
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
        },

        uploadFile() {
            this.isUploading = true;

            // Simulate upload
            setTimeout(() => {
                const newUpload = {
                    id: Date.now(),
                    fileName: this.selectedFile.name,
                    uploadedBy: this.$store.getters.getUser?.firstName || 'Current User',
                    dateUploaded: new Date().toLocaleDateString(),
                    totalCount: Math.floor(Math.random() * 200) + 50,
                    successCount: Math.floor(Math.random() * 150) + 40,
                    errorCount: Math.floor(Math.random() * 10),
                    status: 'COMPLETED'
                };
                this.payrollUpdates.unshift(newUpload);
                this.selectedFile = null;
                this.isUploading = false;
                alert('File uploaded and processed successfully! (API not connected)');
            }, 2000);
        },

        downloadTemplate() {
            const csvContent = 'pension_id,bvn,bank_name,account_number,pension_amount\nKT-PEN-ST-0001,12345678901,Access Bank,0123456789,50000\nKT-PEN-LG-0002,12345678902,First Bank,9876543210,30000';
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'pension_update_template.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        },

        getUploadStatusColor(status) {
            const colors = {
                'COMPLETED': 'success',
                'PENDING': 'warning',
                'FAILED': 'danger',
                'PROCESSING': 'info'
            };
            return colors[status] || 'secondary';
        },

        viewLogs(upload) {
            this.selectedUpload = upload;
            // Mock logs
            this.uploadLogs = [
                { row: 1, pensionId: 'KT-PEN-ST-0001', bvn: '12345678901', status: 'success', message: 'Updated successfully' },
                { row: 2, pensionId: 'KT-PEN-LG-0002', bvn: '12345678902', status: 'success', message: 'Updated successfully' },
                { row: 3, pensionId: 'KT-PEN-ST-0003', bvn: '12345678903', status: 'error', message: 'BVN mismatch' },
                { row: 4, pensionId: 'KT-PEN-LG-0004', bvn: '12345678904', status: 'success', message: 'Updated successfully' },
                { row: 5, pensionId: 'KT-PEN-ST-0005', bvn: '', status: 'error', message: 'Missing BVN' }
            ];
            this.showLogsModal = true;
        },

        exportLogs() {
            // TODO: Implement actual export
            alert('Logs exported (API not connected)');
        }
    }
};
</script>

<style scoped>
/* Filter badges in history table */
.text-xxs {
    font-size: 0.65rem;
}

.gap-1 {
    gap: 0.25rem;
}

.badge.bg-light {
    background-color: #f1f5f9 !important;
    font-weight: 500;
    padding: 0.25rem 0.5rem;
}

/* Header Card Styling */
.header-card {
    border-radius: 1rem !important;
    border: none;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.header-card .card-body {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.icon-box {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 1.25rem;
}

/* Custom Nav Pills */
.nav-pills-custom {
    background: #f1f5f9;
    border-radius: 0.75rem;
    padding: 0.25rem;
}

.nav-pills-custom .nav-item {
    flex: 1;
}

.nav-pills-custom .nav-link {
    padding: 0.6rem 1rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #64748b;
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
}

.nav-pills-custom .nav-link:hover {
    color: #334155;
    background: rgba(255, 255, 255, 0.5);
}

.nav-pills-custom .nav-link.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.nav-pills-custom .nav-link .badge {
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
}

.border-dashed {
    border: 2px dashed #dee2e6;
}

.upload-area {
    transition: all 0.3s ease;
    cursor: pointer;
}

.upload-area:hover {
    border-color: #667eea;
    background-color: #f8f9fa;
}

.modal.show {
    background-color: rgba(0, 0, 0, 0.5);
}

/* Responsive */
@media (max-width: 991px) {
    .nav-pills-custom {
        flex-direction: column;
    }
    
    .nav-pills-custom .nav-link {
        justify-content: flex-start;
    }
}
</style>

